import { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { usePatient } from '@/hooks/use-patients';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { calculateAge, formatDate } from '@/lib/format';

interface PatientPanelProps {
  patientId: string | null | undefined;
  /** Initial collapsed state. Default: collapsed. */
  defaultCollapsed?: boolean;
}

/**
 * Self-contained patient summary panel for the task overview tab.
 * Reads from the existing `usePatient` hook so any improvement to the
 * underlying patient model surfaces here automatically.
 *
 * Renders a one-line summary collapsed (name, age, phone, email, MRN),
 * expanding to a small grid of fields. Lives in its own file so we can
 * iterate on the design without touching the surrounding overview tab.
 */
export function PatientPanel({
  patientId,
  defaultCollapsed = true,
}: PatientPanelProps): React.ReactElement | null {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const { data: patient, isLoading } = usePatient(patientId ?? undefined);

  if (!patientId) return null;
  if (isLoading) {
    return (
      <div className="rounded-lg border bg-card p-4">
        <Skeleton className="h-4 w-48" />
      </div>
    );
  }
  if (!patient) return null;

  const fullName = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim();
  const age = computeAge(patient.dateOfBirth);
  const dob = formatDobShort(patient.dateOfBirth);
  const language = patient.cachedData?.demographics?.data?.preferredLanguage ?? null;
  const insurance = formatInsurance(patient.cachedData?.insurance?.data?.primary);

  // Match the mockup: name+age in a single chunk (comma), then bullet
  // separators between DOB / language / phone / insurance.
  const nameWithAge = age !== null ? `${fullName}, ${age}` : fullName;
  const summaryLine = [nameWithAge, dob, language, patient.phone, insurance]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="rounded-lg border bg-card">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className={cn('flex w-full items-center gap-2 px-4 py-3 text-left', 'hover:bg-accent/50')}
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Patient
        </span>
        <span className="truncate text-sm">{summaryLine || 'Unknown patient'}</span>
      </button>

      {!collapsed && (
        <div className="border-t px-4 py-3">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-3">
            <Field label="Name" value={fullName || '—'} />
            <Field label="Date of birth" value={formatDob(patient.dateOfBirth, age)} />
            <Field label="Phone" value={patient.phone ?? '—'} />
            <Field label="Email" value={patient.email ?? '—'} />
            <Field label="MRN" value={patient.mrn ?? '—'} />
            <Field label="EHR ID" value={patient.ehrPatientId ?? '—'} />
            <Field label="Preferred name" value={patient.preferredName ?? '—'} />
            <Field label="Preferred sex" value={patient.preferredSex ?? '—'} />
            <Field
              label="Last EHR sync"
              value={
                patient.lastEhrSyncAt ? new Date(patient.lastEhrSyncAt).toLocaleString() : 'Never'
              }
            />
          </dl>
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <Link
              to="/patients/$id"
              params={{ id: patient.id }}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Open patient profile
              <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm">{value}</dd>
    </div>
  );
}

function computeAge(dob: string | null | undefined): number | null {
  if (!dob) return null;
  // Delegate to the shared, timezone-safe helper. `new Date("1951-09-18")`
  // parses as UTC midnight and, read in a UTC-negative locale, rolls the
  // day (and occasionally the age at a birthday boundary) back — the bug
  // that made a 9/18 DOB display as 9/17. calculateAge uses parseISO +
  // startOfDay so the date is treated as a timezone-agnostic calendar day.
  const age = calculateAge(dob);
  return Number.isNaN(age) ? null : age;
}

function formatInsurance(
  primary: { payerName?: string; planName?: string } | undefined
): string | null {
  if (!primary) return null;
  // Plan name is what users recognize ("Aetna PPO"). Fall back to payer
  // when plan isn't populated — a common gap on freshly-imported patients.
  return primary.planName?.trim() || primary.payerName?.trim() || null;
}

function formatDobShort(dob: string | null | undefined): string | null {
  if (!dob) return null;
  // formatDate (parseISO + startOfDay) renders the calendar day as
  // stored — no UTC-midnight day-roll. Same "MMM d, yyyy" shape as before.
  const formatted = formatDate(dob);
  return formatted ? `DOB ${formatted}` : null;
}

function formatDob(dob: string | null | undefined, age: number | null): string {
  if (!dob) return '—';
  const formatted = formatDate(dob);
  if (!formatted) return '—';
  return age !== null ? `${formatted} (${age}y)` : formatted;
}
